import Slide from "@ui-kit/disclosure/Slide/Slide.web";
import CheckoutButton from "@shared/components/application/form/CheckoutButton/CheckoutButton.web";
import CurrencyFormat from "@shared/components/ui-kit/data-display/CurrencyFormat/CurrencyFormat.web";
import IconButton from "@shared/components/ui-kit/form/IconButton/IconButton.web";
import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import ScrollContainer from "@shared/components/ui-kit/layout/ScrollContainer/ScrollContainer.web";
import Text from "@shared/components/ui-kit/typography/Text/Text.web";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import {
  calculateSubtotal,
  totalItemsInCart,
} from "@shared/modules/seating/features/BestAvailable/utils/utils";
import { useTranslation } from "@shared/utils/internationalization";
import BestAvailableSeatingCartBody from "../BestAvailableSeatingCartBody/BestAvailableSeatingCartBody.web";
import { BestAvailableSeatingCartContainerProps } from "./BestAvailableSeatingCartContainer.props";

export default function BestAvailableSeatingCartContainer({
  seatingObjectArray,
  onClose,
  onContinue,
  isDisabled,
  isCartOpen = false,
  hideCheckoutButton = false,
}: BestAvailableSeatingCartContainerProps) {
  return (
    <Slide
      direction="right"
      in={isCartOpen}
      style={{
        position: "relative",
        height: "100%",
        width: "100%",
        background: "bg",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <BestAvailableSeatingCartContainer.Content
        seatingObjectArray={seatingObjectArray}
        onClose={onClose}
        onContinue={onContinue}
        isDisabled={isDisabled}
        hideCheckoutButton={hideCheckoutButton}
      />
    </Slide>
  );
}

BestAvailableSeatingCartContainer.Content = function Content({
  seatingObjectArray,
  onClose,
  onContinue,
  isDisabled,
  hideCheckoutButton,
}: BestAvailableSeatingCartContainerProps) {
  const { t } = useTranslation("seating");
  const { currency, emptyBasket, hasItems } = useBasketService();
  const handleEmptySelection = () => {
    if (hasItems) {
      emptyBasket();
    }
    onClose();
  };

  /**
   * handles closing the cart properly to display the seat selection
   * if you remove all the items manually fromt he cart drawer
   * @param removedItemId - the id of the item that was removed
   */
  const handleRemoveSingleItems = (removedItemId: number) => {
    const currentItems = [...seatingObjectArray];
    const expectedNewCount = currentItems.filter(
      (item) => item.id !== removedItemId
    ).length;
    if (expectedNewCount === 0) {
      onClose();
    }
  };

  return (
    <Flex direction="column" height="100%">
      <Flex
        direction="column"
        width="100%"
        gap="xs"
        px="md"
        py={{
          base: "2",
          sm: "4",
        }}
        borderBottom="1px solid"
        borderColor="gray.400"
      >
        <Flex justify="space-between" width="100%" align="center">
          <Text fontWeight="bold" fontSize="lg">
            {t("Selected seats")}
          </Text>
          <IconButton.Ghost
            iconName="Trash"
            width="element.2xl"
            size="md"
            height="element.2xl"
            iconSize="20px"
            iconColor=""
            iconOutline
            aria-label="close"
            onClick={handleEmptySelection}
            data-testid="best-available-empty-selection-button"
          />
        </Flex>
      </Flex>

      <ScrollContainer>
        <BestAvailableSeatingCartBody
          currency={currency}
          onRemoveItem={handleRemoveSingleItems}
        />
      </ScrollContainer>

      <Flex
        p="md"
        borderTop="1px solid"
        borderColor="gray.200"
        bg="bg"
        width="100%"
        boxShadow="0px -4px 8px rgba(0, 0, 0, 0.1)"
        direction="column"
        gap="sm"
        position="sticky"
        bottom="none"
        left="none"
        right="none"
        zIndex="1"
      >
        <Flex justify="space-between" width="100%">
          <Text fontSize="md" fontWeight="bold" color="fg">
            {t("Subtotal")}:
          </Text>
          <Text fontSize="md" fontWeight="bold" color="fg">
            <CurrencyFormat
              currency={currency}
              value={calculateSubtotal(seatingObjectArray)}
              displayType="text"
              data-testid="best-available-subtotal"
            />
          </Text>
        </Flex>
        {!hideCheckoutButton && (
          <CheckoutButton
            shouldCheckout={false}
            isDisabled={isDisabled || !onContinue}
            onCheckout={onContinue ?? (() => null)}
            quantityToDisplay={totalItemsInCart(seatingObjectArray)}
            data-testid="best-available-continue-button"
          />
        )}
      </Flex>
    </Flex>
  );
};
